// Live Call Copilot (addendum §3–§12). Browser-safe schemas for routes-copilot.ts and the realtime stream.
// Everything about Aircall live transcription is UNVERIFIED until a real AI Assist Pro account is tested
// («sin verificar»); payload fields are read defensively and unknown shapes are kept as receipts, never guessed.
import { z } from 'zod';
import { QUALIFICATION_FIELDS } from './prompts.js';

const Iso = z.string();
const Id = z.string().min(1).max(64);
const Text = (max: number) => z.string().trim().max(max);

// ───────────── Playbooks ─────────────

export const PLAYBOOK_STAGES = ['opening', 'discovery', 'qualification', 'project_fit', 'objections', 'appointment_offer', 'closing'] as const;
export const PlaybookStageSchema = z.enum(PLAYBOOK_STAGES);
export type PlaybookStage = z.infer<typeof PlaybookStageSchema>;
export const PLAYBOOK_STAGE_LABELS: Record<PlaybookStage, string> = {
  opening: 'Apertura',
  discovery: 'Descubrimiento',
  qualification: 'Calificación',
  project_fit: 'Ajuste al proyecto',
  objections: 'Objeciones',
  appointment_offer: 'Oferta de cita',
  closing: 'Cierre',
};

/** P0 conditions use controlled fields/operators only — never executable code. */
export const PLAYBOOK_CONDITION_FIELDS = ['call_direction', 'project_selected', ...QUALIFICATION_FIELDS] as const;
export const PlaybookConditionFieldSchema = z.enum(PLAYBOOK_CONDITION_FIELDS);
export const PLAYBOOK_CONDITION_OPERATORS = ['equals', 'not_equals', 'is_known', 'is_unknown'] as const;
export const PlaybookConditionOperatorSchema = z.enum(PLAYBOOK_CONDITION_OPERATORS);
export const PlaybookConditionSchema = z
  .strictObject({ field: PlaybookConditionFieldSchema, operator: PlaybookConditionOperatorSchema, value: Text(120).optional() })
  .refine((c) => (c.operator === 'equals' || c.operator === 'not_equals' ? !!c.value : true), { message: 'La condición necesita un valor' });
export type PlaybookCondition = z.infer<typeof PlaybookConditionSchema>;

export const PlaybookStepSchema = z.strictObject({
  /** Stable across versions: observations and evidence are keyed by it. */
  key: z.string().regex(/^[a-z0-9_]{2,40}$/, 'Usa minúsculas, números y guion bajo (2–40)'),
  stage: PlaybookStageSchema,
  title: Text(120).min(1),
  instructions: Text(2000).default(''),
  suggestedQuestion: Text(500).nullable().default(null),
  required: z.boolean().default(false),
  condition: PlaybookConditionSchema.nullable().default(null),
  /** Phrases that, heard in the transcript, are evidence the step was covered (accent/case-insensitive). */
  evidenceCriteria: z.array(Text(200).min(2)).max(20).default([]),
});
export type PlaybookStep = z.infer<typeof PlaybookStepSchema>;

export const PlaybookObjectionSchema = z.strictObject({
  key: z.string().regex(/^[a-z0-9_]{2,40}$/),
  /** Phrases that identify the objection in the caller's words. */
  triggers: z.array(Text(200).min(2)).min(1).max(20),
  response: Text(2000).min(1),
});

export const PlaybookContentSchema = z
  .strictObject({
    openingGuidance: Text(4000).default(''),
    steps: z.array(PlaybookStepSchema).max(60).default([]),
    qualificationFields: z.array(z.enum(QUALIFICATION_FIELDS)).max(QUALIFICATION_FIELDS.length).default([]),
    objections: z.array(PlaybookObjectionSchema).max(40).default([]),
    forbiddenClaims: z.array(Text(300).min(2)).max(40).default([]),
    escalationRules: z.array(Text(500).min(2)).max(30).default([]),
    closingObjective: Text(1000).default(''),
  })
  .refine((c) => new Set(c.steps.map((s) => s.key)).size === c.steps.length, { message: 'Cada paso necesita una clave única' })
  .refine((c) => new Set(c.objections.map((s) => s.key)).size === c.objections.length, { message: 'Cada objeción necesita una clave única' });
export type PlaybookContent = z.infer<typeof PlaybookContentSchema>;

export const PLAYBOOK_VERSION_STATUSES = ['draft', 'tested', 'published', 'archived'] as const;
export const PlaybookVersionStatusSchema = z.enum(PLAYBOOK_VERSION_STATUSES);
export type PlaybookVersionStatus = z.infer<typeof PlaybookVersionStatusSchema>;
export const PLAYBOOK_VERSION_STATUS_LABELS: Record<PlaybookVersionStatus, string> = { draft: 'Borrador', tested: 'Probado', published: 'Publicado', archived: 'Archivado' };

export const PlaybookMappingSchema = z.strictObject({
  projectId: Id.nullable().default(null),
  /** Phone line (ChannelEndpoint of kind aircall_line or telnyx_line). */
  endpointId: Id.nullable().default(null),
  /** Free-text team label (no team entity exists yet). */
  team: Text(80).nullable().default(null),
  isDefault: z.boolean().default(false),
});
export type PlaybookMapping = z.infer<typeof PlaybookMappingSchema>;

export const PlaybookVersionSchema = z.object({
  id: Id,
  playbookId: Id,
  version: z.number().int(),
  status: PlaybookVersionStatusSchema,
  content: PlaybookContentSchema,
  changeNote: z.string().nullable(),
  createdAt: Iso,
  testedAt: Iso.nullable(),
  publishedAt: Iso.nullable(),
  archivedAt: Iso.nullable(),
});
export type PlaybookVersion = z.infer<typeof PlaybookVersionSchema>;

export const PlaybookSchema = z.object({
  id: Id,
  title: z.string(),
  purpose: z.string(),
  language: z.string(),
  archived: z.boolean(),
  mappings: z.array(PlaybookMappingSchema),
  publishedVersion: z.number().int().nullable(),
  latestVersion: z.number().int().nullable(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type Playbook = z.infer<typeof PlaybookSchema>;
export const PlaybookDetailSchema = PlaybookSchema.extend({ versions: z.array(PlaybookVersionSchema) });

export const CreatePlaybookSchema = z.strictObject({
  title: Text(160).min(2),
  purpose: Text(1000).default(''),
  language: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/).default('es'),
  mappings: z.array(PlaybookMappingSchema).max(50).default([]),
  content: PlaybookContentSchema.default({ openingGuidance: '', steps: [], qualificationFields: [], objections: [], forbiddenClaims: [], escalationRules: [], closingObjective: '' }),
});
export const UpdatePlaybookSchema = z.strictObject({
  title: Text(160).min(2).optional(),
  purpose: Text(1000).optional(),
  language: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/).optional(),
  mappings: z.array(PlaybookMappingSchema).max(50).optional(),
});
export const SavePlaybookDraftSchema = z.strictObject({ content: PlaybookContentSchema, changeNote: Text(500).nullable().optional() });
export const PlaybookVersionActionSchema = z.strictObject({ note: Text(500).nullable().optional() });

// ───────────── Configuration (advisor / line mapping, policy) ─────────────

export const CopilotSuggestionPolicySchema = z.strictObject({
  /** Minimum seconds between two model-generated suggestions for the same session. */
  cooldownSeconds: z.number().int().min(0).max(600).default(20),
  /** Recent transcript turns sent to the model (bounded). */
  recentTurns: z.number().int().min(2).max(40).default(12),
  /** Secondary suggestions shown under the primary one (addendum §6: at most two). */
  maxSecondary: z.number().int().min(0).max(2).default(2),
  /** Allow coaching on sessions whose project is not selected (general guidance only, no project facts). */
  generalGuidanceWithoutProject: z.boolean().default(true),
});
export type CopilotSuggestionPolicy = z.infer<typeof CopilotSuggestionPolicySchema>;

export const CopilotAdvisorMappingSchema = z.object({
  id: Id,
  connectionId: Id,
  providerUserId: z.string(),
  membershipId: Id,
  advisorName: z.string().nullable(),
  /** Declared by an admin; Aircall entitlement is not verified by the platform («sin verificar»). */
  licensed: z.boolean(),
});
export const CopilotLineMappingSchema = z.object({
  id: Id,
  endpointId: Id,
  lineName: z.string(),
  /** Declared by an admin (Aircall live transcription per number) — «sin verificar». */
  liveTranscription: z.boolean(),
  language: z.string(),
});

export const CopilotConfigSchema = z.object({
  coachingEnabled: z.boolean(),
  policy: CopilotSuggestionPolicySchema,
  advisors: z.array(CopilotAdvisorMappingSchema),
  lines: z.array(CopilotLineMappingSchema),
  /** Honest capability status: live transcription has never been verified on a real account. */
  verification: z.enum(['unverified', 'verified']),
  verificationNote: z.string(),
});
export type CopilotConfig = z.infer<typeof CopilotConfigSchema>;

export const UpdateCopilotConfigSchema = z.strictObject({
  coachingEnabled: z.boolean().optional(),
  policy: CopilotSuggestionPolicySchema.partial().optional(),
  advisors: z
    .array(z.strictObject({ connectionId: Id, providerUserId: z.string().trim().min(1).max(64), membershipId: Id, licensed: z.boolean().default(false) }))
    .max(500)
    .optional(),
  lines: z.array(z.strictObject({ endpointId: Id, liveTranscription: z.boolean().default(false), language: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/).default('es') })).max(200).optional(),
});

// ───────────── Sessions ─────────────

export const COPILOT_SESSION_STATES = ['waiting', 'live', 'ended'] as const;
export const CopilotSessionStateSchema = z.enum(COPILOT_SESSION_STATES);
export type CopilotSessionState = z.infer<typeof CopilotSessionStateSchema>;
export const COPILOT_COACHING_STATES = ['active', 'needs_selection', 'unavailable', 'paused', 'budget_exceeded'] as const;
export const CopilotCoachingStateSchema = z.enum(COPILOT_COACHING_STATES);
export type CopilotCoachingState = z.infer<typeof CopilotCoachingStateSchema>;
export const COPILOT_COACHING_LABELS: Record<CopilotCoachingState, string> = {
  active: 'Asistencia activa',
  needs_selection: 'Selecciona proyecto o playbook',
  unavailable: 'Asistencia en vivo no disponible',
  paused: 'Asistencia pausada',
  budget_exceeded: 'Límite de uso del copiloto alcanzado',
};

/** Where the live speech of a copilot session comes from (one ingestion core for all of them). */
export const TRANSCRIPT_SOURCE_KINDS = ['aircall_realtime', 'telnyx_stream', 'simulator'] as const;
export const TranscriptSourceKindSchema = z.enum(TRANSCRIPT_SOURCE_KINDS);
export type TranscriptSourceKind = z.infer<typeof TranscriptSourceKindSchema>;
export const TRANSCRIPT_SOURCE_LABELS: Record<TranscriptSourceKind, string> = {
  aircall_realtime: 'Aircall (transcripción en vivo)',
  telnyx_stream: 'Telnyx (transcripción propia)',
  simulator: 'Simulación',
};

export const SPEAKERS = ['internal', 'external', 'unknown'] as const;
export const SpeakerSchema = z.enum(SPEAKERS);
export type Speaker = z.infer<typeof SpeakerSchema>;

export const UtteranceSchema = z.object({
  id: Id,
  speaker: SpeakerSchema,
  text: z.string(),
  startMs: z.number().int().nullable(),
  endMs: z.number().int().nullable(),
  sourceAt: Iso.nullable(),
  receivedAt: Iso,
  /** Arrived after the call ended: a post-call revision (never reopens live coaching). */
  postCall: z.boolean(),
});
export type Utterance = z.infer<typeof UtteranceSchema>;

export const STEP_STATES = ['not_started', 'in_progress', 'covered', 'needs_verification', 'not_applicable'] as const;
export const StepStateSchema = z.enum(STEP_STATES);
export type StepState = z.infer<typeof StepStateSchema>;
export const STEP_STATE_LABELS: Record<StepState, string> = {
  not_started: 'Pendiente',
  in_progress: 'En curso',
  covered: 'Cubierto',
  needs_verification: 'Por verificar',
  not_applicable: 'No aplica',
};

export const EvidenceRefSchema = z.object({ utteranceId: Id, excerpt: z.string() });
export const StepObservationSchema = z.object({
  playbookVersionId: Id,
  stepKey: z.string(),
  title: z.string(),
  stage: PlaybookStageSchema,
  required: z.boolean(),
  state: StepStateSchema,
  evidence: z.array(EvidenceRefSchema),
  source: z.enum(['auto', 'manual']),
  correctedBy: z.string().nullable(),
  correctedAt: Iso.nullable(),
  note: z.string().nullable(),
});
export type StepObservation = z.infer<typeof StepObservationSchema>;

export const SUGGESTION_TYPES = ['answer', 'objection', 'next_question', 'reminder', 'escalation'] as const;
export const SuggestionTypeSchema = z.enum(SUGGESTION_TYPES);
export type SuggestionType = z.infer<typeof SuggestionTypeSchema>;
export const SUGGESTION_TYPE_LABELS: Record<SuggestionType, string> = {
  answer: 'Respuesta',
  objection: 'Objeción',
  next_question: 'Siguiente pregunta',
  reminder: 'Recordatorio',
  escalation: 'Escalar',
};
export const SUGGESTION_STATUSES = ['active', 'superseded', 'dismissed', 'used', 'discarded_stale', 'withdrawn'] as const;
export const SuggestionStatusSchema = z.enum(SUGGESTION_STATUSES);
/** Practical verification states instead of model confidence percentages (addendum §6). */
export const VERIFICATION_STATES = ['verified', 'incomplete', 'needs_confirmation'] as const;
export const VerificationStateSchema = z.enum(VERIFICATION_STATES);
export const VERIFICATION_LABELS: Record<z.infer<typeof VerificationStateSchema>, string> = {
  verified: 'Fuente verificada',
  incomplete: 'Información incompleta',
  needs_confirmation: 'Necesita confirmación',
};

export const SuggestionSourceSchema = z.object({
  ref: z.string(),
  kind: z.enum(['document', 'project', 'unit', 'price', 'availability', 'playbook', 'contact']),
  label: z.string(),
  /** Mutable facts carry their freshness and source timestamp. */
  freshness: z.enum(['fresh', 'stale', 'unknown']).nullable(),
  verifiedAt: Iso.nullable(),
  documentVersionId: z.string().nullable(),
  page: z.number().int().nullable(),
});
export type SuggestionSource = z.infer<typeof SuggestionSourceSchema>;

export const SuggestionSchema = z.object({
  id: Id,
  type: SuggestionTypeSchema,
  priority: z.enum(['primary', 'secondary']),
  text: z.string(),
  triggerExcerpt: z.string().nullable(),
  sources: z.array(SuggestionSourceSchema),
  verification: VerificationStateSchema,
  status: SuggestionStatusSchema,
  /** Deterministic (playbook rules) vs model-generated; simulated = scripted test model, never real AI. */
  origin: z.enum(['rules', 'model']),
  simulated: z.boolean(),
  latencyMs: z.number().int().nullable(),
  contextRevision: z.number().int(),
  createdAt: Iso,
  /** A cited source was deleted: the card text is withheld (addendum §11). */
  sourceRemoved: z.boolean(),
});
export type Suggestion = z.infer<typeof SuggestionSchema>;

export const CopilotParticipantSchema = z.object({
  providerUserId: z.string().nullable(),
  membershipId: z.string().nullable(),
  name: z.string().nullable(),
  from: Iso,
  to: Iso.nullable(),
});

export const CopilotSessionSummarySchema = z.object({
  id: Id,
  /** simulator = practice session (no provider call); shown as «Simulación». */
  source: TranscriptSourceKindSchema,
  callUuid: z.string().nullable(),
  providerCallId: z.string().nullable(),
  state: CopilotSessionStateSchema,
  coaching: CopilotCoachingStateSchema,
  coachingReason: z.string().nullable(),
  direction: z.enum(['inbound', 'outbound']).nullable(),
  contact: z.object({ id: Id, name: z.string().nullable() }).nullable(),
  project: z.object({ id: Id, name: z.string() }).nullable(),
  advisor: z.object({ membershipId: Id, name: z.string().nullable() }).nullable(),
  lineName: z.string().nullable(),
  playbook: z.object({ playbookId: Id, versionId: Id, title: z.string(), version: z.number().int() }).nullable(),
  /** Project/playbook candidates when the mapping is ambiguous (advisor must choose). */
  candidates: z.object({ projects: z.array(z.object({ id: Id, name: z.string() })), playbooks: z.array(z.object({ playbookId: Id, title: z.string() })) }),
  seq: z.number().int(),
  contextRevision: z.number().int(),
  startedAt: Iso,
  answeredAt: Iso.nullable(),
  endedAt: Iso.nullable(),
  lastUtteranceAt: Iso.nullable(),
  postCallStatus: z.enum(['none', 'pending', 'ready', 'unavailable', 'reviewed']),
  /** Why the session was closed without a provider end event (reconciliation, plan A4); null for normal ends. */
  endReason: z.string().nullable(),
});
export type CopilotSessionSummary = z.infer<typeof CopilotSessionSummarySchema>;

/** Whether model suggestions can be generated at all (playbook-rule cards never need a model). */
export const CopilotModelStatusSchema = z.object({
  /** enabled = real model; simulated = deterministic test/demo model («Modelo simulado»); disabled = none configured. */
  state: z.enum(['enabled', 'disabled', 'simulated']),
  reason: z.string().nullable(),
});
export type CopilotModelStatus = z.infer<typeof CopilotModelStatusSchema>;

export const CopilotSnapshotSchema = z.object({
  session: CopilotSessionSummarySchema,
  /** Copilot model state when the snapshot was built (live screen tells the advisor honestly when there is none). */
  model: CopilotModelStatusSchema,
  participants: z.array(CopilotParticipantSchema),
  /** Null when the viewer may not read the transcript (recording rights). */
  transcript: z.array(UtteranceSchema).nullable(),
  observations: z.array(StepObservationSchema),
  suggestions: z.array(SuggestionSchema),
});
export type CopilotSnapshot = z.infer<typeof CopilotSnapshotSchema>;

export const CopilotSessionListSchema = z.object({ items: z.array(CopilotSessionSummarySchema) });
export const CopilotSessionListQuerySchema = z.object({
  state: CopilotSessionStateSchema.optional(),
  projectId: Id.optional(),
  source: TranscriptSourceKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export const SelectSessionContextSchema = z.strictObject({
  projectId: Id.nullable().optional(),
  playbookId: Id.nullable().optional(),
  reason: Text(300).nullable().optional(),
});
export const CorrectObservationSchema = z.strictObject({
  state: StepStateSchema,
  note: Text(500).nullable().optional(),
});
export const SetCoachingSchema = z.strictObject({ active: z.boolean() });

export const FEEDBACK_ACTIONS = ['used', 'dismissed', 'pinned', 'helpful', 'not_helpful', 'sources_opened'] as const;
export const FeedbackActionSchema = z.enum(FEEDBACK_ACTIONS);
export const SuggestionFeedbackInputSchema = z.strictObject({ action: FeedbackActionSchema, reason: Text(300).nullable().optional() });
export const SuggestionFeedbackResultSchema = z.object({ suggestion: SuggestionSchema, recorded: z.boolean() });

export const CopilotKnowledgeSearchSchema = z.strictObject({ query: Text(300).min(2) });
export const CopilotKnowledgeResultSchema = z.object({
  passages: z.array(z.object({ text: z.string(), source: SuggestionSourceSchema })),
  structuredHint: z.string().nullable(),
});

// ───────────── Post-call review ─────────────

export const PostCallQualificationSchema = z.object({
  field: z.enum(QUALIFICATION_FIELDS),
  value: z.string(),
  evidence: z.array(EvidenceRefSchema),
  decision: z.enum(['pending', 'accepted', 'rejected']),
});
/**
 * CRM writeback of the REVIEWED post-call (plan A4): only advisor-confirmed items, through the CRM connection's
 * writeback policy (outbox, idempotent). none = nothing requested yet; skipped = not written, with the reason.
 */
export const POST_CALL_CRM_WRITEBACK_STATES = ['none', 'pending', 'written', 'skipped', 'failed'] as const;
export const PostCallCrmWritebackSchema = z.object({
  status: z.enum(POST_CALL_CRM_WRITEBACK_STATES),
  reason: z.string().nullable(),
  updatedAt: Iso.nullable(),
});
export type PostCallCrmWriteback = z.infer<typeof PostCallCrmWritebackSchema>;
export const POST_CALL_CRM_WRITEBACK_LABELS: Record<PostCallCrmWriteback['status'], string> = {
  none: 'Sin envío al CRM',
  pending: 'Enviando al CRM',
  written: 'Escrito en el CRM',
  skipped: 'No se escribió en el CRM',
  failed: 'Error al escribir en el CRM',
};

export const PostCallSchema = z.object({
  status: z.enum(['none', 'pending', 'ready', 'unavailable', 'reviewed']),
  reason: z.string().nullable(),
  summary: z.string().nullable(),
  qualification: z.array(PostCallQualificationSchema),
  unresolvedQuestions: z.array(z.string()),
  followUp: z.string().nullable(),
  coverage: z.object({ required: z.number().int(), covered: z.number().int(), notApplicable: z.number().int() }),
  simulated: z.boolean(),
  reviewedAt: Iso.nullable(),
  reviewedBy: z.string().nullable(),
  /** Writes are recommendations until the advisor confirms them; nothing was sent or booked by the copilot. */
  appliedWrites: z.array(z.string()),
  crmWriteback: PostCallCrmWritebackSchema,
});
export type PostCall = z.infer<typeof PostCallSchema>;
export const ReviewPostCallSchema = z.strictObject({
  /** Qualification facts the advisor confirms (written to the lead through the normal qualification path). */
  accept: z.array(z.enum(QUALIFICATION_FIELDS)).max(QUALIFICATION_FIELDS.length).default([]),
  reject: z.array(z.enum(QUALIFICATION_FIELDS)).max(QUALIFICATION_FIELDS.length).default([]),
  summary: Text(8000).nullable().optional(),
});

// ───────────── Realtime stream (WebSocket) ─────────────

export const COPILOT_EVENT_TYPES = [
  'session_updated',
  'utterances_added',
  'observations_updated',
  'suggestion_added',
  'suggestion_updated',
  'suggestions_cleared',
  'access_changed',
  'post_call_ready',
  /** Delivery-only no-op (never logged): a logged event with nothing for this viewer (e.g. a suggestion discarded as
   *  stale) still occupies its `seq`, so the server sends `{ skipped: true }` to keep the sequence contiguous. */
  'skipped',
] as const;
export const CopilotEventTypeSchema = z.enum(COPILOT_EVENT_TYPES);
export type CopilotEventType = z.infer<typeof CopilotEventTypeSchema>;
/** Event types that are written to the session event log (everything except the delivery-only `skipped`). */
export type CopilotLoggedEventType = Exclude<CopilotEventType, 'skipped'>;

/** Server → client frames. `seq` is the session's monotonic sequence; a client reconnects with `after=<seq>`. */
export const CopilotServerFrameSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('snapshot'), seq: z.number().int(), snapshot: CopilotSnapshotSchema }),
  z.object({ type: z.literal('event'), seq: z.number().int(), event: CopilotEventTypeSchema, data: z.unknown() }),
  z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
  z.object({ type: z.literal('pong') }),
]);
export type CopilotServerFrame = z.infer<typeof CopilotServerFrameSchema>;
export const COPILOT_STREAM_PATH = '/api/v1/workspaces/:workspaceId/copilot/sessions/:sessionId/stream';
/** Close codes: unauthorized/forbidden/not found are terminal for the client (no blind reconnect loop). */
export const COPILOT_WS_CLOSE = { unauthorized: 4401, forbidden: 4403, notFound: 4404, originRejected: 4400 } as const;

/** Compiled copilot instructions (application rules → global copilot guidance → playbook/project layers). */
export const CopilotInstructionsSchema = z.object({
  globalVersion: z.number().int().nullable(),
  layers: z.array(z.object({ key: z.string(), title: z.string(), version: z.string().nullable(), text: z.string() })),
  compiledText: z.string(),
});

// ───────────── Local call simulator (plan A2; dev/test only, refused in production) ─────────────

/** Scripted practice calls replayed through the same ingestion path. Simulator sessions (`source: 'simulator'`) are
 *  labeled «Simulación» and never count as validation of a provider or as coached calls in reports. */
export const CopilotSimulatorScenarioSchema = z.object({ key: z.string(), title: z.string(), description: z.string() });
export type CopilotSimulatorScenario = z.infer<typeof CopilotSimulatorScenarioSchema>;
export const CopilotSimulatorAvailabilitySchema = z.object({
  available: z.boolean(),
  /** Spanish reason when unavailable (production, module off); null when available. */
  reason: z.string().nullable(),
  scenarios: z.array(CopilotSimulatorScenarioSchema),
});
export type CopilotSimulatorAvailability = z.infer<typeof CopilotSimulatorAvailabilitySchema>;
export const StartCopilotSimulationSchema = z.strictObject({
  scenario: z.string().min(1).max(64),
  projectId: Id.optional(),
});
export type StartCopilotSimulation = z.infer<typeof StartCopilotSimulationSchema>;
export const CopilotSimulationStartedSchema = z.object({ sessionId: z.string() });
export type CopilotSimulationStarted = z.infer<typeof CopilotSimulationStartedSchema>;
// ───────────── Reports (plan A4) ─────────────

const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Usa el formato AAAA-MM-DD');

export const CopilotReportQuerySchema = z.object({
  /** Inclusive local dates in the workspace timezone (session start). Default: the last 30 days. */
  from: Day.optional(),
  to: Day.optional(),
  projectId: Id.optional(),
});

export const COPILOT_FAILURE_KINDS = [
  'suggestion_model_error',
  'suggestion_invalid_output',
  'suggestion_llm_disabled',
  'suggestion_budget_exceeded',
  'post_call_failed',
  'missing_end_event',
  'transcript_quarantined',
  'transcript_failed',
] as const;
export const CopilotFailureKindSchema = z.enum(COPILOT_FAILURE_KINDS);
export type CopilotFailureKind = z.infer<typeof CopilotFailureKindSchema>;
export const COPILOT_FAILURE_LABELS: Record<CopilotFailureKind, string> = {
  suggestion_model_error: 'Error del modelo al generar una sugerencia',
  suggestion_invalid_output: 'El modelo devolvió una sugerencia inválida',
  suggestion_llm_disabled: 'Sin modelo configurado para sugerencias',
  suggestion_budget_exceeded: 'Presupuesto de asistencia agotado',
  post_call_failed: 'No se pudo generar el borrador posterior a la llamada',
  missing_end_event: 'Sesión cerrada por conciliación (sin evento de fin)',
  transcript_quarantined: 'Transcripción en cuarentena (formato no reconocido)',
  transcript_failed: 'Error al procesar una transcripción',
};

/** Simulator sessions never count (practice, not validation). Unknown values are null, never 0. */
export const CopilotReportSchema = z.object({
  range: z.object({ from: Day, to: Day, start: Iso, end: Iso, timezone: z.string() }),
  filters: z.object({ projectId: Id.nullable() }),
  /** Coaching rests on unverified provider transcription («sin verificar»). */
  verification: z.literal('unverified'),
  sessions: z.object({
    /** Real (non-simulator) sessions started in the range. */
    total: z.number().int(),
    /** Sessions where live assistance was on at some point (active/paused/budget, or a suggestion was shown). */
    coached: z.number().int(),
    /** Closed by reconciliation without a provider end event. */
    closedByReconcile: z.number().int(),
  }),
  requiredSteps: z.object({
    /** Coached sessions with a pinned playbook. */
    sessions: z.number().int(),
    required: z.number().int(),
    covered: z.number().int(),
    notApplicable: z.number().int(),
    /** covered / (required − notApplicable); null without required steps. */
    coverage: z.number().nullable(),
  }),
  feedback: z.object({
    /** Suggestions shown to the advisor (stale discarded work is never shown). */
    shown: z.number().int(),
    used: z.number().int(),
    dismissed: z.number().int(),
    pinned: z.number().int(),
  }),
  grounding: z.object({
    withVerifiedSources: z.number().int(),
    withoutVerifiedSources: z.number().int(),
    /** Share of shown suggestions with verified sources; null when nothing was shown. */
    coverage: z.number().nullable(),
  }),
  latency: z.object({
    /** Webhook receipt → suggestion persisted, in ms. null when there are no measured samples (unknown ≠ 0). */
    p50Ms: z.number().nullable(),
    p95Ms: z.number().nullable(),
    samples: z.number().int(),
    /** Shown suggestions without a measurement (older data or no webhook receipt). */
    unmeasured: z.number().int(),
  }),
  postCall: z.object({
    reviewed: z.number().int(),
    unresolvedQuestions: z.number().int(),
  }),
  failures: z.array(z.object({ kind: CopilotFailureKindSchema, label: z.string(), count: z.number().int() })),
  notes: z.array(z.string()),
});
export type CopilotReport = z.infer<typeof CopilotReportSchema>;

// ───────────── AI-assisted playbook import (plan A4) ─────────────

export const PlaybookImportProposalInputSchema = z.strictObject({
  text: z.string().trim().min(1, 'Pega el texto del guion').max(20_000, 'Máximo 20,000 caracteres'),
  title: Text(200).optional(),
});
/** Only a PROPOSAL: nothing is saved (the editor saves it through the draft route after review). */
export const PlaybookImportProposalSchema = z.object({
  proposal: PlaybookContentSchema,
  notes: z.array(z.string()),
  modelUsed: z.boolean(),
});
export type PlaybookImportProposal = z.infer<typeof PlaybookImportProposalSchema>;
